import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Layout, Button, Typography, message, Modal, Form, Input, DatePicker, Space, Select, Divider, Checkbox } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import LocationPane from '../components/LocationList/LocationPane';
import CollapsedRail from '../components/Layout/CollapsedRail';
import IssueListPane from '../components/IssueList/IssueListPane';
import CommandPalette from '../components/CommandPalette';
import { useTriage } from '../contexts/TriageContext';
import { useTriageParams } from '../hooks/useTriageParams';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useViewportWidth } from '../hooks/useViewportWidth';
import { FLEET_CLASS_IDS, TRIAGE_ACTIONS } from '../types/triage';
import type { LocationWithCounts, Ticket, Task, Action, TriageActionDef, CreateTaskPayload } from '../types/triage';
import {
  fetchTickets,
  fetchTicketCounts,
  fetchNodeDetail,
  fetchNodeHistory,
  fetchNodeFirmwareHistory,
  fetchNodeTestJigReports,
  fetchLocationLogs,
  fetchNodeLogs,
  addLocationLog,
  addNodeLog,
  triageTicket,
  fetchTasksForTicket,
  createTask,
  fetchActions,
  createAction,
  fetchIssues,
  fetchIssueCounts,
  fetchIssueAlerts,
  fetchIssue,
  fetchTicket,
  fetchTasksForIssue,
  triageIssue,
  reclassifyIssue,
  reclassifyAllIssues,
  fetchReclassifyAllStatus,
  claimIssue,
  releaseIssue,
  ApiError,
} from '../config/api';
import IssueDetailPane from '../components/IssueList/IssueDetailPane';
import { isClaimFresh } from '../components/triageMaps';
import TriageTour, { hasSeenTour, markTourSeen } from '../components/Onboarding/TriageTour';
import { trackEvent } from '../lib/analytics';
import type { Issue, IssueAlert } from '../types/triage';

const { Sider, Content } = Layout;

// Maps triage action key to a descriptive task title template.
// Room/key is appended when available.
const TASK_TITLE_MAP: Record<string, string> = {
  hardware: 'Replace node',
  hardware_eol: 'Replace EOL node',
  tampering: 'Investigate damaged node',
  firmware: 'Update firmware',
  out_of_range: 'Relocate node',
  on_site: 'Investigate node on-site',
};

function buildTaskTitle(actionKey: string, room: string): string {
  const verb = TASK_TITLE_MAP[actionKey] || actionKey;
  return room ? `${verb} — ${room}` : verb;
}

interface TriagePageProps {
  mode: 'triage' | 'issues';
}

const TriagePage: React.FC<TriagePageProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { isAdmin, user } = useCurrentUser();
  const currentUserEmail = user?.email ?? user?.sub ?? null;

  // Pick the next issue to land on after triaging/advancing. Prefer the first
  // issue at/after fromIdx that ISN'T freshly claimed by a peer, so two
  // operators draining the same priority-sorted queue don't collide on the
  // same top item. Falls back to the index-based next if everything left is
  // claimed by others.
  // NOTE: declared HERE, above handleConfirm/handleIssueAction, on purpose —
  // those list it in their useCallback dependency arrays, which are evaluated
  // during render at their declaration point. If pickNextIssue were declared
  // below them (as it originally was), that dep-array read hit a `const` still
  // in its temporal dead zone → "Cannot access before initialization" in the
  // minified prod bundle (esbuild ordering surfaced it; dev tolerated it).
  const pickNextIssue = useCallback((remaining: Issue[], fromIdx: number): Issue | null => {
    if (remaining.length === 0) return null;
    const blocked = (i: Issue) =>
      isClaimFresh(i.claimed_at) && i.claimed_by != null && i.claimed_by !== currentUserEmail;
    const start = Math.max(0, fromIdx);
    for (let k = start; k < remaining.length; k++) if (!blocked(remaining[k])) return remaining[k];
    for (let k = 0; k < start; k++) if (!blocked(remaining[k])) return remaining[k];
    return remaining[Math.min(fromIdx, remaining.length - 1)] ?? null;
  }, [currentUserEmail]);
  const {
    selectedLocationId,
    selectedTicketId,
    selectLocation,
    selectTicket,
    ticketListRef,
  } = useTriage();

  const { locationId: urlLocationId, statusFilter, classFilter, sortOrder, issueId: urlIssueId, setParam } = useTriageParams();
  // Local state is the source of truth for the selected issue. URL is only
  // used for the initial seed (deep-link support) and for back/forward sync
  // (UX-012, via the effect below); after mount, clicks update local state
  // directly and sync the URL as a side effect.
  const [issueId, setIssueId] = useState<number | null>(urlIssueId);
  const [messageApi, contextHolder] = message.useMessage();

  // Previous-URL refs — only sync state ← URL when the URL ITSELF changed
  // (back/forward, deep link, command palette). Without this, when the user
  // picks a location locally, react-router's setSearchParams and React's
  // useState commit in DIFFERENT render cycles. The effect would see
  // selectedLocationId='B' alongside a still-stale urlLocationId='cassia'
  // and "sync" by reverting state to 'cassia' (observed 2026-05-25 —
  // regression introduced in fef2b59 when this sync changed from mount-only
  // to live; the original 835a766 explicitly warned about it).
  //
  // Sentinel `undefined` means "not yet initialized", so the very first
  // effect run (mount) ALWAYS syncs from URL — preserves deep-link support.
  const prevUrlLocRef = useRef<string | null | undefined>(undefined);
  const prevUrlIssueRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (prevUrlLocRef.current === urlLocationId) return;
    prevUrlLocRef.current = urlLocationId;
    if (urlLocationId && urlLocationId !== selectedLocationId) {
      selectLocation(urlLocationId);
    }
  }, [urlLocationId, selectedLocationId, selectLocation]);

  useEffect(() => {
    if (prevUrlIssueRef.current === urlIssueId) return;
    prevUrlIssueRef.current = urlIssueId;
    if (urlIssueId && urlIssueId !== issueId) {
      setIssueId(urlIssueId);
    }
  }, [urlIssueId, issueId]);

  // --- Locations ---
  const [locations, setLocations] = useState<LocationWithCounts[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  // First-run onboarding tour. Auto-opens once per browser after the first
  // location list loads; re-openable from Help (via /triage?tour=1). Gated on
  // localStorage so returning users aren't nagged.
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    if (mode !== 'triage' || locationsLoading || locations.length === 0) return;
    const forced = new URLSearchParams(window.location.search).get('tour') === '1';
    if (!forced && hasSeenTour()) return;
    // Open the tour. If nothing is selected yet, select the first location so
    // its first issue auto-selects (effect below) and the detail pane mounts —
    // that's what the likely-cause + action-bar tour steps spotlight. Without a
    // selection those two steps fall back to centered. Don't override a
    // selection the user already has (e.g. re-launching the tour from Help).
    if (!selectedLocationId && locations[0]) selectLocation(locations[0].location_id);
    setTourOpen(true);
    // Record "seen" the moment it opens, not just on finish. If the popover
    // fails to paint on the user's client, the tour is a one-time nuisance
    // instead of a mask that re-traps them on every reload. Re-launch from
    // Help still works via the ?tour=1 forced path above.
    markTourSeen();
  }, [mode, locationsLoading, locations.length, selectedLocationId, selectLocation]);
  const closeTour = useCallback(() => {
    setTourOpen(false);
    markTourSeen();
  }, []);

  const loadLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const [ticketCounts, issueCounts] = await Promise.all([
        fetchTicketCounts(),
        fetchIssueCounts().catch(() => [] as { location_id: string; location_name: string; ticket_class_id: number | null; count: number }[]),
      ]);
      const byLocation = new Map<string, LocationWithCounts>();
      for (const row of ticketCounts) {
        let loc = byLocation.get(row.location_id);
        if (!loc) {
          loc = {
            location_id: row.location_id,
            location_name: row.location_name,
            ticketCounts: { total: 0, byClass: {} },
          };
          byLocation.set(row.location_id, loc);
        }
        loc.ticketCounts.byClass[row.ticket_class_id] = row.count;
        loc.ticketCounts.total += row.count;
      }
      for (const row of issueCounts) {
        let loc = byLocation.get(row.location_id);
        if (!loc) {
          loc = {
            location_id: row.location_id,
            location_name: row.location_name,
            ticketCounts: { total: 0, byClass: {} },
          };
          byLocation.set(row.location_id, loc);
        }
        if (!loc.issueCounts) loc.issueCounts = {
          total: 0, suggested: 0, confirmed: 0,
          byClass: {}, byClassSuggested: {}, byClassConfirmed: {},
        };
        if (row.ticket_class_id != null) {
          loc.issueCounts.byClass[row.ticket_class_id] =
            (loc.issueCounts.byClass[row.ticket_class_id] ?? 0) + row.count;
          if (row.stage === 'suggested') {
            loc.issueCounts.byClassSuggested[row.ticket_class_id] =
              (loc.issueCounts.byClassSuggested[row.ticket_class_id] ?? 0) + row.count;
          } else if (row.stage === 'confirmed') {
            loc.issueCounts.byClassConfirmed[row.ticket_class_id] =
              (loc.issueCounts.byClassConfirmed[row.ticket_class_id] ?? 0) + row.count;
          }
        }
        loc.issueCounts.total += row.count;
        if (row.stage === 'suggested') loc.issueCounts.suggested += row.count;
        else if (row.stage === 'confirmed') loc.issueCounts.confirmed += row.count;
      }
      setLocations(Array.from(byLocation.values()).sort((a, b) => b.ticketCounts.total - a.ticketCounts.total));
    } catch (err: any) {
      messageApi.error(`Failed to load locations: ${err.message}`);
    } finally {
      setLocationsLoading(false);
    }
  }, [messageApi]);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  // Auto-select the first location on cold load so the operator lands
  // straight in a populated queue instead of an empty "select a location"
  // state. Only fires once per mount (guarded by the ref) and only when
  // there's no deep-linked location (?loc=) and nothing already selected —
  // the tour effect above may have already selected one, and back/forward
  // nav shouldn't be overridden.
  const autoSelectedInitialLocRef = useRef(false);
  useEffect(() => {
    if (locationsLoading || autoSelectedInitialLocRef.current) return;
    if (locations.length === 0) return;
    autoSelectedInitialLocRef.current = true;
    if (!selectedLocationId && !urlLocationId) {
      selectLocation(locations[0].location_id);
    }
  }, [locationsLoading, locations, selectedLocationId, urlLocationId, selectLocation]);

  // --- Tickets ---
  const TICKETS_PAGE_SIZE = 200;
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsHasMore, setTicketsHasMore] = useState(false);
  const [ticketsLoadingMore, setTicketsLoadingMore] = useState(false);
  const ticketsOffsetRef = useRef(0);

  // Guards against a fast location/filter switch racing itself: without this,
  // clicking between locations quickly fires overlapping fetchTickets() calls
  // and whichever resolves LAST wins, even if it's for a location the operator
  // has since navigated away from -- silently showing the wrong ticket list
  // (reported 2026-07-12, "especially when clicking between pages fast").
  // loadMoreTickets already guarded its own race via ticketsOffsetRef; this is
  // the same pattern applied to the initial per-location/filter load.
  const ticketsRequestIdRef = useRef(0);
  const loadTickets = useCallback(async () => {
    const requestId = ++ticketsRequestIdRef.current;
    if (!selectedLocationId) {
      setTickets([]);
      setTicketsHasMore(false);
      ticketsOffsetRef.current = 0;
      return;
    }
    setTicketsLoading(true);
    ticketsOffsetRef.current = 0;
    try {
      const data = await fetchTickets({
        location_id: selectedLocationId,
        class_ids: classFilter ? String(classFilter) : FLEET_CLASS_IDS.join(','),
        status: statusFilter,
        sort: sortOrder,
        limit: TICKETS_PAGE_SIZE,
        offset: 0,
      });
      if (ticketsRequestIdRef.current !== requestId) return;
      setTickets(data);
      setTicketsHasMore(data.length === TICKETS_PAGE_SIZE);
      ticketsOffsetRef.current = data.length;
    } catch (err: any) {
      if (ticketsRequestIdRef.current !== requestId) return;
      messageApi.error(`Failed to load tickets: ${err.message}`);
    } finally {
      if (ticketsRequestIdRef.current === requestId) setTicketsLoading(false);
    }
  }, [selectedLocationId, classFilter, statusFilter, sortOrder, messageApi]);

  const loadMoreTickets = useCallback(async () => {
    if (!selectedLocationId || ticketsLoadingMore || !ticketsHasMore) return;
    setTicketsLoadingMore(true);
    const offsetAtRequest = ticketsOffsetRef.current;
    try {
      const data = await fetchTickets({
        location_id: selectedLocationId,
        class_ids: classFilter ? String(classFilter) : FLEET_CLASS_IDS.join(','),
        status: statusFilter,
        sort: sortOrder,
        limit: TICKETS_PAGE_SIZE,
        offset: offsetAtRequest,
      });
      // Guard against stale responses after a filter/location change reset the offset
      if (ticketsOffsetRef.current !== offsetAtRequest) return;
      setTickets((prev) => [...prev, ...data]);
      setTicketsHasMore(data.length === TICKETS_PAGE_SIZE);
      ticketsOffsetRef.current = offsetAtRequest + data.length;
    } catch (err: any) {
      messageApi.error(`Failed to load more tickets: ${err.message}`);
    } finally {
      setTicketsLoadingMore(false);
    }
  }, [selectedLocationId, classFilter, statusFilter, sortOrder, ticketsHasMore, ticketsLoadingMore, messageApi]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  // --- Issues ---
  const ISSUES_PAGE_SIZE = 200;
  const [issues, setIssues] = useState<Issue[]>([]);
  // Default to loading so the middle pane shows a Skeleton on cold mount
  // instead of flashing the "No issues" Empty state before the first fetch
  // gets a chance to call setIssuesLoading(true).
  const [issuesLoading, setIssuesLoading] = useState(true);
  const [issuesHasMore, setIssuesHasMore] = useState(false);
  const [issuesLoadingMore, setIssuesLoadingMore] = useState(false);
  const issuesOffsetRef = useRef(0);

  const stageParam: 'suggested' | 'confirmed' = mode === 'triage' ? 'suggested' : 'confirmed';

  // Bulk selection (confirmed /issues only). A gateway-outage cohort is many
  // identical-disposition issues; without this the operator dispositions each
  // one-by-one. Reset whenever the underlying queue changes.
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<number>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  useEffect(() => { setSelectedIssueIds(new Set()); }, [mode, selectedLocationId, classFilter, stageParam]);
  const toggleSelectIssue = useCallback((id: number) => {
    setSelectedIssueIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Same race guard as loadTickets above, and load-bearing here specifically:
  // a stale `issues` array from an overlapping fetch feeds straight into the
  // auto-select-first-issue effect below, which would then wrongly conclude
  // a deep-linked issue "isn't in the current queue view" (or auto-select the
  // wrong first issue) based on data for a location the operator already
  // navigated away from.
  const issuesRequestIdRef = useRef(0);
  const loadIssues = useCallback(async () => {
    const requestId = ++issuesRequestIdRef.current;
    if (!selectedLocationId) {
      setIssues([]);
      setIssuesHasMore(false);
      issuesOffsetRef.current = 0;
      return;
    }
    setIssuesLoading(true);
    issuesOffsetRef.current = 0;
    try {
      const data = await fetchIssues({
        location_id: selectedLocationId,
        class_ids: classFilter ? String(classFilter) : FLEET_CLASS_IDS.join(','),
        stage: stageParam,
        sort: sortOrder,
        limit: ISSUES_PAGE_SIZE,
        offset: 0,
      });
      if (issuesRequestIdRef.current !== requestId) return;
      setIssues(data);
      setIssuesHasMore(data.length === ISSUES_PAGE_SIZE);
      issuesOffsetRef.current = data.length;
    } catch (err: any) {
      if (issuesRequestIdRef.current !== requestId) return;
      messageApi.error(`Failed to load issues: ${err.message}`);
    } finally {
      if (issuesRequestIdRef.current === requestId) setIssuesLoading(false);
    }
  }, [selectedLocationId, classFilter, stageParam, sortOrder, messageApi]);

  const loadMoreIssues = useCallback(async () => {
    if (!selectedLocationId || issuesLoadingMore || !issuesHasMore) return;
    setIssuesLoadingMore(true);
    const offsetAtRequest = issuesOffsetRef.current;
    try {
      const data = await fetchIssues({
        location_id: selectedLocationId,
        class_ids: classFilter ? String(classFilter) : FLEET_CLASS_IDS.join(','),
        stage: stageParam,
        sort: sortOrder,
        limit: ISSUES_PAGE_SIZE,
        offset: offsetAtRequest,
      });
      if (issuesOffsetRef.current !== offsetAtRequest) return;
      setIssues((prev) => [...prev, ...data]);
      setIssuesHasMore(data.length === ISSUES_PAGE_SIZE);
      issuesOffsetRef.current = offsetAtRequest + data.length;
    } catch (err: any) {
      messageApi.error(`Failed to load more issues: ${err.message}`);
    } finally {
      setIssuesLoadingMore(false);
    }
  }, [selectedLocationId, classFilter, stageParam, sortOrder, issuesHasMore, issuesLoadingMore, messageApi]);

  // Reload when location, filters, sort, or view/stage changes.
  useEffect(() => { void loadIssues(); }, [loadIssues]);

  // Auto-select the first issue when location changes (operator no longer
  // has to click into the queue after picking a new location). Tracked by
  // ref so the auto-select fires exactly once per location change, not on
  // every filter/sort tweak or after-triage list shrink.
  //
  // Deep-link preservation only applies to the FIRST location this effect
  // ever evaluates (cold page load) — not to every subsequent manual
  // location switch. This matters because handleSelectLocation (App.tsx)
  // only updates the `loc` URL param, not `issue` — so urlIssueId stays
  // stale forever after any deep link is used once. Without isInitialEvalRef,
  // comparing urlIssueId === issueId would keep matching on every later
  // manual location click too, permanently breaking "auto-select this
  // location's first issue" for the rest of the session (reported
  // 2026-07-12). A still-valid deep-link issueId in the new list is always
  // preserved regardless (the `issues.some(...)` check above this comment).
  const isInitialLocationEvalRef = useRef(true);
  const lastAutoSelectLocRef = useRef<string | null>(null);
  useEffect(() => {
    if (issuesLoading) return;
    if (lastAutoSelectLocRef.current === selectedLocationId) return;
    const isInitialEval = isInitialLocationEvalRef.current;
    lastAutoSelectLocRef.current = selectedLocationId;
    isInitialLocationEvalRef.current = false;
    if (!selectedLocationId || issues.length === 0) return;
    if (issueId && issues.some((i) => i.issue_id === issueId)) return;
    // A deep-linked issue (?issue=NNN) that isn't in this location's
    // currently-filtered queue (wrong status/class, or past the first page)
    // used to be silently replaced with issues[0] here — the operator would
    // land on an unrelated ticket with no indication anything had changed
    // (observed 2026-07-12: ?issue=204 silently became issue 19). The detail
    // pane fetches selectedIssue independently by id regardless of whether
    // it's in this list, so keep showing the requested issue and only warn
    // instead of substituting a different one -- but only on cold load.
    if (isInitialEval && issueId && urlIssueId === issueId) {
      messageApi.warning(`Issue #${issueId} isn't in this location's current queue view (different status or class filter) — showing it directly.`);
      return;
    }
    const first = issues[0];
    setIssueId(first.issue_id);
    setParam('issue', String(first.issue_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuesLoading, selectedLocationId, issues, urlIssueId, messageApi]);

  // --- Issue detail (issues route) ---
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [issueAlerts, setIssueAlerts] = useState<IssueAlert[]>([]);
  const [issueAlertsLoading, setIssueAlertsLoading] = useState(false);
  const [issueDetailLoading, setIssueDetailLoading] = useState(false);
  const [reclassifyLoading, setReclassifyLoading] = useState(false);
  // Bumped after a triage/reclassify write to nudge AgentPredictionCard
  // (lives inside IssueDetailPane) to refetch and show post-write verdict.
  const [predictionRefreshKey, setPredictionRefreshKey] = useState(0);
  // UX-040: delayed second-bump after reclassify. Tracked in a ref so we
  // can cancel it on unmount or on another reclassify (don't fire state
  // updates on an unmounted parent or stack pending refreshes).
  const predictionRefreshDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (predictionRefreshDelayRef.current) {
      clearTimeout(predictionRefreshDelayRef.current);
    }
  }, []);

  // decision_latency_ms = ms from first-time-the-operator-saw-this-issue to
  // when they triaged it. Use a map keyed by issue_id so re-selection (back/
  // forward, command-palette deep-link, list re-fetch) doesn't reset the
  // clock for an issue the operator has already been looking at. Bumped to
  // a ref-of-map so updates don't trigger re-renders.
  const issueFirstSeenRef = useRef<Map<number, number>>(new Map());
  useEffect(() => {
    if (!selectedIssue) return;
    const map = issueFirstSeenRef.current;
    if (!map.has(selectedIssue.issue_id)) {
      map.set(selectedIssue.issue_id, Date.now());
    }
  }, [selectedIssue?.issue_id]);

  useEffect(() => {
    if (!issueId) {
      setSelectedIssue(null);
      setIssueAlerts([]);
      return;
    }
    let cancelled = false;
    setIssueDetailLoading(true);
    setIssueAlertsLoading(true);
    Promise.all([
      fetchIssue(issueId),
      fetchIssueAlerts(issueId),
    ])
      .then(([issue, alerts]) => {
        if (cancelled) return;
        setSelectedIssue(issue);
        setIssueAlerts(alerts);
      })
      .catch((err) => {
        if (cancelled) return;
        messageApi.error(`Failed to load issue: ${err.message}`);
        setSelectedIssue(null);
        setIssueAlerts([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIssueDetailLoading(false);
        setIssueAlertsLoading(false);
      });
    return () => { cancelled = true; };
  }, [issueId, messageApi]);

  const handleBackfillIssues = useCallback(async () => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL?.trim() || 'http://localhost:3002'}/api/issues/backfill`, { method: 'POST' });
      await Promise.all([loadIssues(), loadLocations()]);
      messageApi.success('Classifier sweep complete');
    } catch (err: any) {
      messageApi.error(`Backfill failed: ${err.message}`);
    }
  }, [loadIssues, loadLocations, messageApi]);

  const [reclassifyAllRunning, setReclassifyAllRunning] = useState(false);
  const handleReclassifyAll = useCallback(async (opts: { skipLlm: boolean } = { skipLlm: false }) => {
    setReclassifyAllRunning(true);
    const suffix = opts.skipLlm ? ' (heuristics only, LLM bypassed)' : '';
    try {
      // The sweep runs as a background job and can take minutes at fleet scale
      // (a single synchronous request would be dropped by the gateway idle
      // timeout mid-flight). Kick it off, then poll the status endpoint.
      await reclassifyAllIssues({ skip_llm: opts.skipLlm });
      messageApi.info('Reclassification started — sweeping all open issues in the background…');
      let job = null as Awaited<ReturnType<typeof fetchReclassifyAllStatus>> | null;
      for (let i = 0; i < 180; i++) {
        await new Promise((r) => setTimeout(r, 4000)); // ~12 min ceiling
        const s = await fetchReclassifyAllStatus();
        if (s.status !== 'running') { job = s; break; }
      }
      await Promise.all([loadIssues(), loadLocations()]);
      if (!job) {
        messageApi.warning('Reclassification is still running — reopen later to see the result.');
      } else if (job.status === 'error') {
        messageApi.error(`Reclassify failed: ${job.error ?? 'unknown error'}`);
      } else {
        const auditWarn = job.auditErrors ? `, ${job.auditErrors} audit-log write(s) failed` : '';
        messageApi.success(
          `Reclassified ${job.updated ?? 0} of ${job.scanned ?? 0} issues to ${job.classifier_version ?? ''}${job.errors ? ` (${job.errors} errors)` : ''}${auditWarn}${suffix}`,
        );
      }
    } catch (err: any) {
      messageApi.error(`Reclassify failed: ${err.message}`);
    } finally {
      setReclassifyAllRunning(false);
    }
  }, [loadIssues, loadLocations, messageApi]);

  // Keep ticket list ref in sync
  useEffect(() => { ticketListRef.current = tickets; }, [tickets, ticketListRef]);

  // --- Node detail ---
  const [nodeDetail, setNodeDetail] = useState<any>(null);
  const [batteryStatus, setBatteryStatus] = useState<any>(null);
  // #5: battery snapshot as of the offline moment (canonical for diagnosis).
  const [batteryAtOffline, setBatteryAtOffline] = useState<any>(null);
  const [gateway, setGateway] = useState<any>(null);
  // Recovery state pulled from sensorflow_analytics.m_node_recovery_lookup via fetchNodeDetail.
  // NodeInfoBar surfaces is_recovered + time_to_recovery_days for the operator.
  const [recovery, setRecovery] = useState<any>(null);
  const [nodeHistory, setNodeHistory] = useState<any[]>([]);
  const [firmwareHistory, setFirmwareHistory] = useState<any[]>([]);
  const [testJigReports, setTestJigReports] = useState<any[]>([]);
  const [locationLogs, setLocationLogs] = useState<any[]>([]);
  const [nodeLogs, setNodeLogs] = useState<any[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [pastTickets, setPastTickets] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pendingTaskInfo, setPendingTaskInfo] = useState<{ ticket: Ticket; action: TriageActionDef } | null>(null);
  const [pendingIssueTaskInfo, setPendingIssueTaskInfo] = useState<{ issue: Issue; action: TriageActionDef } | null>(null);
  const [taskPromptCreating, setTaskPromptCreating] = useState(false);
  const [taskPromptForm] = Form.useForm();

  // Undo state
  const [undoInfo, setUndoInfo] = useState<{
    ticket: Ticket;
    index: number;
    originalClassId: number;
    originalStatusId: number;
    originalActionId: number | null;
    originalRootCauseId: number | null;
  } | null>(null);
  const undoInfoRef = useRef(undoInfo);
  undoInfoRef.current = undoInfo;

  // Session counter state
  const [sessionCount, setSessionCount] = useState(0);
  const [sessionStart, setSessionStart] = useState<number | null>(null);

  // Shortcut help state
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);

  // Command palette state
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  // Skip task prompt helpers
  const getSkippedActions = (): Set<string> => {
    try { return new Set(JSON.parse(localStorage.getItem('triage-skip-task-actions') || '[]')); }
    catch { return new Set(); }
  };
  const toggleSkipAction = (actionKey: string) => {
    const skipped = getSkippedActions();
    if (skipped.has(actionKey)) skipped.delete(actionKey);
    else skipped.add(actionKey);
    localStorage.setItem('triage-skip-task-actions', JSON.stringify([...skipped]));
  };

  // --- Actions list for task prompt ---
  const [actions, setActions] = useState<Action[]>([]);
  const [newActionName, setNewActionName] = useState('');
  const [creatingAction, setCreatingAction] = useState(false);

  useEffect(() => {
    fetchActions().then(setActions).catch(() => {});
  }, []);

  const handleAddAction = async () => {
    if (!newActionName.trim()) return;
    setCreatingAction(true);
    try {
      const created = await createAction(newActionName.trim());
      setActions((prev) =>
        prev.some((a) => a.id === created.id) ? prev : [...prev, created].sort((a, b) => a.action.localeCompare(b.action))
      );
      setNewActionName('');
      taskPromptForm.setFieldValue('action_id', created.id);
    } catch (err: any) {
      messageApi.error(err.message);
    } finally {
      setCreatingAction(false);
    }
  };

  const actionDropdownRender = (menu: React.ReactNode) => (
    <>
      {menu}
      <Divider style={{ margin: '8px 0' }} />
      <Space style={{ padding: '0 8px 4px' }}>
        <Input
          size="small"
          placeholder="New action name"
          value={newActionName}
          onChange={(e) => setNewActionName(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <Button size="small" type="text" icon={<PlusOutlined />} loading={creatingAction} onClick={handleAddAction}>
          Add
        </Button>
      </Space>
    </>
  );

  const EXECUTOR_OPTIONS = [
    { value: 'Contractor', label: 'Contractor' },
    { value: 'Hotel Engineering', label: 'Hotel Engineering' },
    { value: 'Project coordinator', label: 'Project Coordinator' },
  ];

  // Selected ticket: prefer the row from the loaded tickets[] list; otherwise fetch it
  // directly so clicking an issue (whose rep alert may be outside the current filters)
  // still drives the detail pane.
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  useEffect(() => {
    if (!selectedTicketId) {
      setSelectedTicket(null);
      return;
    }
    const fromList = tickets.find((t) => t.ticket_id === selectedTicketId);
    if (fromList) {
      setSelectedTicket(fromList);
      return;
    }
    let cancelled = false;
    fetchTicket(selectedTicketId)
      .then((t) => { if (!cancelled) setSelectedTicket(t); })
      .catch(() => { if (!cancelled) setSelectedTicket(null); });
    return () => { cancelled = true; };
  }, [selectedTicketId, tickets]);

  // Both detail-fetch effects below (ticket flow + issue flow) write into the
  // same node-detail state slots. Each effect had its own `cancelled` flag,
  // but neither cancelled the OTHER — quick ticket→issue navigation could
  // resolve the slow ticket fetch after the fast issue fetch and overwrite
  // the issue's data. A shared monotonic counter prevents that: each effect
  // increments + captures the new value, only commits if the counter hasn't
  // moved on.
  const detailFetchSeqRef = useRef(0);

  useEffect(() => {
    if (!selectedTicket) {
      setNodeDetail(null);
      setBatteryStatus(null);
      setBatteryAtOffline(null);
      setGateway(null);
      setRecovery(null);
      setNodeHistory([]);
      setFirmwareHistory([]);
      setTestJigReports([]);
      setLocationLogs([]);
      setNodeLogs([]);
      setTasks([]);
      setPastTickets([]);
      if (!selectedTicketId) setDetailLoading(false);
      return;
    }

    const seq = ++detailFetchSeqRef.current;
    setDetailLoading(true);

    const macId = selectedTicket.observable_id;
    const locId = selectedTicket.location_id;

    const ticketAsOf = selectedTicket.ticket_created
      ? dayjs(selectedTicket.ticket_created).format('YYYY-MM-DD')
      : null;
    Promise.all([
      fetchNodeDetail(macId, ticketAsOf).catch(() => ({ node: null, mappingHistory: [], batteryStatus: null, batteryAtOffline: null, gateway: null, pastTickets: [], recovery: null })),
      fetchNodeHistory(macId).catch(() => []),
      fetchNodeFirmwareHistory(macId).catch(() => []),
      fetchNodeTestJigReports(macId).catch(() => []),
      fetchLocationLogs(locId).catch(() => []),
      fetchNodeLogs(macId).catch(() => []),
      fetchTasksForTicket(selectedTicket.ticket_id).catch(() => []),
    ]).then(([detail, history, fwHistory, tjReports, locLogs, nLogs, ticketTasks]) => {
      if (seq !== detailFetchSeqRef.current) return;
      setNodeDetail(detail.node);
      setBatteryStatus(detail.batteryStatus);
      setBatteryAtOffline(detail.batteryAtOffline ?? null);
      setGateway(detail.gateway);
      setRecovery(detail.recovery ?? null);
      setPastTickets(detail.pastTickets || []);
      setNodeHistory(history);
      setFirmwareHistory(fwHistory);
      setTestJigReports(tjReports);
      setLocationLogs(locLogs);
      setNodeLogs(nLogs);
      setTasks(ticketTasks);
      setDetailLoading(false);
    });
  }, [selectedTicket]);

  // Issue view: drive node context from the issue's observable + load issue-scoped tasks.
  useEffect(() => {
    // Node context is needed whenever an issue row is selected, regardless of
    // whether we're in the Triage (suggested) or Issues (confirmed) route.
    if (!selectedIssue) {
      setNodeDetail(null);
      setBatteryStatus(null);
      setBatteryAtOffline(null);
      setGateway(null);
      setRecovery(null);
      setNodeHistory([]);
      setFirmwareHistory([]);
      setTestJigReports([]);
      setLocationLogs([]);
      setNodeLogs([]);
      setTasks([]);
      setPastTickets([]);
      return;
    }
    const seq = ++detailFetchSeqRef.current;
    setDetailLoading(true);
    const macId = selectedIssue.observable_id;
    const locId = selectedIssue.location_id;
    const issueAsOf = selectedIssue.created_at
      ? dayjs(selectedIssue.created_at).format('YYYY-MM-DD')
      : null;
    Promise.all([
      fetchNodeDetail(macId, issueAsOf).catch(() => ({ node: null, mappingHistory: [], batteryStatus: null, batteryAtOffline: null, gateway: null, pastTickets: [], recovery: null })),
      fetchNodeHistory(macId).catch(() => []),
      fetchNodeFirmwareHistory(macId).catch(() => []),
      fetchNodeTestJigReports(macId).catch(() => []),
      fetchLocationLogs(locId).catch(() => []),
      fetchNodeLogs(macId).catch(() => []),
      fetchTasksForIssue(selectedIssue.issue_id).catch(() => []),
    ]).then(([detail, history, fwHistory, tjReports, locLogs, nLogs, issueTasks]) => {
      if (seq !== detailFetchSeqRef.current) return;
      setNodeDetail(detail.node);
      setBatteryStatus(detail.batteryStatus);
      setBatteryAtOffline(detail.batteryAtOffline ?? null);
      setGateway(detail.gateway);
      setRecovery(detail.recovery ?? null);
      setPastTickets(detail.pastTickets || []);
      setNodeHistory(history);
      setFirmwareHistory(fwHistory);
      setTestJigReports(tjReports);
      setLocationLogs(locLogs);
      setNodeLogs(nLogs);
      setTasks(issueTasks);
      setDetailLoading(false);
    });
  }, [selectedIssue]);

  // --- Confirm suggestion (Nodes → Issues) ---
  const handleConfirm = useCallback(async () => {
    if (!selectedIssue) return;
    try {
      // Phase 0 label capture on Confirm (v2, 2026-05-25). Confirm without a
      // class change = accepted_suggestion=true; we don't have a separate
      // "original suggestion" to compare against here, but the rule's
      // current class IS that suggestion (operator hasn't changed it).
      // The server backfills agent_class_id and computes agent_match.
      const suggestedClassId = selectedIssue.ticket_class_id ?? null;
      const firstSeenAt = issueFirstSeenRef.current.get(selectedIssue.issue_id);
      const decisionLatencyMs = firstSeenAt ? Date.now() - firstSeenAt : null;
      await triageIssue(selectedIssue.issue_id, {
        status_id: 7,
        accepted_suggestion: suggestedClassId != null ? true : null,
        suggested_class_id: suggestedClassId,
        decision_latency_ms: decisionLatencyMs,
      });
      trackEvent('issue_confirmed', {
        issue_id: selectedIssue.issue_id,
        location_id: selectedIssue.location_id ?? null,
        ticket_class_id: suggestedClassId,
        confidence: selectedIssue.confidence ?? null,
        decision_latency_ms: decisionLatencyMs,
        mode,
      });
      messageApi.success(`Confirmed — issue #${selectedIssue.issue_id} promoted to Issues`);

      // Suggest a follow-up task based on the confirmed class. TRIAGE_ACTIONS
      // already maps class → actionId, so we can reuse the same mapping that
      // the Alerts tab used to open the task-create modal.
      const classId = selectedIssue.ticket_class_id;
      const suggestedAction = classId != null
        ? TRIAGE_ACTIONS.find((a) => a.ticketClassId === classId && a.actionId != null)
        : null;
      if (suggestedAction) {
        const room = selectedIssue.key_name || selectedIssue.autoset_name || '';
        taskPromptForm.setFieldsValue({
          title: buildTaskTitle(suggestedAction.key, room),
          action_id: suggestedAction.actionId ?? undefined,
          executor: localStorage.getItem(`triage-executor-${selectedIssue.location_id}`) || 'Contractor',
          description: undefined,
          due_date: undefined,
        });
        setPendingIssueTaskInfo({ issue: selectedIssue, action: suggestedAction });
      } else if (classId == null) {
        // Confirmed with no class → no task suggestion possible. Tell the
        // operator so the confirm doesn't feel silently "lost". They can
        // still create a task manually from the Actions menu.
        messageApi.info('No class set — confirm completed, but no follow-up task suggested. Set a class first to get a task recommendation.');
      }

      if (mode === 'triage') {
        // Remove the confirmed issue from the triage queue and advance to
        // the next one at the same index, mirroring handleIssueAction. The
        // operator stays on /triage to keep working through their queue —
        // previously we redirected to /issues, which yanked them out of
        // their flow on every confirm.
        const confirmedId = selectedIssue.issue_id;
        setIssues((prev) => {
          const idx = prev.findIndex((i) => i.issue_id === confirmedId);
          const remaining = prev.filter((i) => i.issue_id !== confirmedId);
          const next = pickNextIssue(remaining, idx);
          setIssueId(next ? next.issue_id : null);
          setParam('issue', next ? String(next.issue_id) : null);
          return remaining;
        });
      } else {
        // Already on /issues: refetch detail to reflect new status.
        const fresh = await fetchIssue(selectedIssue.issue_id);
        setSelectedIssue(fresh);
      }
      loadLocations();
    } catch (err: any) {
      messageApi.error(`Confirm failed: ${err?.message || 'Unknown error'}`);
    }
  }, [selectedIssue, mode, navigate, loadLocations, messageApi, taskPromptForm, pickNextIssue]);

  // Create the suggested task for a newly-confirmed issue.
  const handleIssueTaskCreate = useCallback(async (values: any) => {
    if (!pendingIssueTaskInfo) return;
    setTaskPromptCreating(true);
    try {
      const payload: CreateTaskPayload = {
        issue_id: pendingIssueTaskInfo.issue.issue_id,
        title: values.title,
        description: values.description || undefined,
        due_date: values.due_date ? values.due_date.toISOString() : undefined,
        action_id: values.action_id ?? pendingIssueTaskInfo.action.actionId ?? undefined,
        executor: values.executor || undefined,
        parent_entity_type: 'node_to_slot_mapping',
        parent_entity_id: pendingIssueTaskInfo.issue.observable_id,
      };
      await createTask(payload);
      if (values.executor && pendingIssueTaskInfo.issue.location_id) {
        localStorage.setItem(`triage-executor-${pendingIssueTaskInfo.issue.location_id}`, values.executor);
      }
      trackEvent('task_created', {
        issue_id: pendingIssueTaskInfo.issue.issue_id,
        location_id: pendingIssueTaskInfo.issue.location_id ?? null,
        action_id: payload.action_id ?? null,
        action_key: pendingIssueTaskInfo.action.key,
        executor: values.executor ?? null,
        source: 'post_confirm',
      });
      messageApi.success('Task created');
    } catch (err: any) {
      messageApi.error(`Failed to create task: ${err.message}`);
    } finally {
      setTaskPromptCreating(false);
      setPendingIssueTaskInfo(null);
      taskPromptForm.resetFields();
    }
  }, [pendingIssueTaskInfo, messageApi, taskPromptForm]);

  // --- Issue-level triage + reclassify ---
  const handleIssueAction = useCallback(async (actionDef: TriageActionDef, comment?: string) => {
    if (!selectedIssue) return;
    try {
      // Phase 0 label capture (only meaningful when this triage closes the
      // issue; the server ignores these fields otherwise).
      const suggestedClassId = selectedIssue.ticket_class_id ?? null;
      const finalClassId = actionDef.ticketClassId ?? null;
      const acceptedSuggestion = suggestedClassId != null && finalClassId != null
        ? suggestedClassId === finalClassId
        : null;
      const firstSeenAt = issueFirstSeenRef.current.get(selectedIssue.issue_id);
      const decisionLatencyMs = firstSeenAt ? Date.now() - firstSeenAt : null;

      await triageIssue(selectedIssue.issue_id, {
        ticket_class_id: actionDef.ticketClassId ?? undefined,
        action_id: actionDef.actionId ?? undefined,
        root_cause_id: actionDef.rootCauseId ?? undefined,
        status_id: actionDef.statusId,
        comment,
        accepted_suggestion: acceptedSuggestion,
        suggested_class_id: suggestedClassId,
        decision_latency_ms: decisionLatencyMs,
      });
      // Nudge AgentPredictionCard to refetch — the server may have re-run the
      // sidecar on the new rule verdict (see /reclassify notifyTriageAgent).
      setPredictionRefreshKey((k) => k + 1);

      // Track the class decision. acceptedSuggestion=false means the operator
      // overrode the rule — high-info signal for funnels.
      trackEvent('class_changed', {
        issue_id: selectedIssue.issue_id,
        location_id: selectedIssue.location_id ?? null,
        from_class_id: suggestedClassId,
        to_class_id: finalClassId,
        action_key: actionDef.key,
        accepted_suggestion: acceptedSuggestion,
        decision_latency_ms: decisionLatencyMs,
      });

      // Session counter parity with handleAction (audit 2026-05-25): the
      // counter banner used to read "0 triaged this session" on /issues
      // because only the legacy ticket flow incremented it.
      setSessionCount((c) => c + 1);
      if (!sessionStart) setSessionStart(Date.now());

      // Prompt task creation for actions that have a concrete follow-up
      // (replace node, update firmware, on-site investigation, etc.). Skips
      // close/false-alert (no follow-up needed) and battery (no canonical
      // action wired). Previously this prompt only opened on Confirm, so
      // operators picking an action straight from the bar lost the task-
      // creation suggestion entirely.
      if (actionDef.actionId && !getSkippedActions().has(actionDef.key)) {
        const room = selectedIssue.key_name || selectedIssue.autoset_name || '';
        taskPromptForm.setFieldsValue({
          title: buildTaskTitle(actionDef.key, room),
          action_id: actionDef.actionId ?? undefined,
          executor: localStorage.getItem(`triage-executor-${selectedIssue.location_id}`) || 'Contractor',
          description: undefined,
          due_date: undefined,
        });
        setPendingIssueTaskInfo({ issue: selectedIssue, action: actionDef });
      }

      // Remove from the issues list (it's now closed or reclassified) and
      // advance to the next UNCLAIMED issue (skip peers' in-flight work).
      setIssues((prev) => {
        const idx = prev.findIndex((i) => i.issue_id === selectedIssue.issue_id);
        const remaining = prev.filter((i) => i.issue_id !== selectedIssue.issue_id);
        const next = pickNextIssue(remaining, idx);
        setIssueId(next ? next.issue_id : null);
        setParam('issue', next ? String(next.issue_id) : null);
        return remaining;
      });
      loadLocations();
    } catch (err: any) {
      // Tier 4B forcing function (2026-05-26): server returns 400 with
      // `class_required_on_close` when an operator tries to close an
      // issue that has no ticket_class_id on file. Surface a clearer
      // toast than the raw error so they know what to do.
      const msg = err?.message || 'Unknown error';
      if (typeof msg === 'string' && /class_required_on_close/i.test(msg)) {
        messageApi.error('Set a class on this issue first — close requires a class so it counts as a labeled example.');
      } else {
        messageApi.error(`Issue triage failed: ${msg}`);
      }
      throw err;
    }
  }, [selectedIssue, setParam, loadLocations, messageApi, taskPromptForm, pickNextIssue]);

  // Apply one disposition to every selected issue (confirmed /issues view).
  // Per-id triage calls via allSettled so one failure (e.g. close needs a
  // class) doesn't abort the batch; the summary reports partial success.
  const handleBulkAction = useCallback(async (actionDef: TriageActionDef) => {
    const ids = Array.from(selectedIssueIds);
    if (ids.length === 0) return;
    setBulkRunning(true);
    try {
      const results = await Promise.allSettled(ids.map((id) =>
        triageIssue(id, {
          ticket_class_id: actionDef.ticketClassId ?? undefined,
          action_id: actionDef.actionId ?? undefined,
          root_cause_id: actionDef.rootCauseId ?? undefined,
          status_id: actionDef.statusId,
        })));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      trackEvent('bulk_action', { action_key: actionDef.key, count: ids.length, ok, failed });
      if (ok) messageApi.success(`${actionDef.label}: applied to ${ok} issue${ok === 1 ? '' : 's'}${failed ? `, ${failed} failed` : ''}`);
      else messageApi.error(`Bulk ${actionDef.label} failed for all ${failed} — a class may be required before close.`);
      setSelectedIssueIds(new Set());
      await Promise.all([loadIssues(), loadLocations()]);
    } finally {
      setBulkRunning(false);
    }
  }, [selectedIssueIds, loadIssues, loadLocations, messageApi]);

  const handleReclassify = useCallback(async () => {
    if (!selectedIssue) return;
    setReclassifyLoading(true);
    try {
      await reclassifyIssue(selectedIssue.issue_id, true);
      const [issue, alerts] = await Promise.all([
        fetchIssue(selectedIssue.issue_id),
        fetchIssueAlerts(selectedIssue.issue_id),
      ]);
      setSelectedIssue(issue);
      setIssueAlerts(alerts);
      setIssues((prev) => prev.map((i) => (i.issue_id === issue.issue_id ? issue : i)));
      // Server side-effects: notifyTriageAgent was dispatched fire-and-forget,
      // so the sidecar's new prediction may take ~1-3s to land. Bump the
      // refresh key now and again after a short delay to catch both states.
      // UX-040: tracked timer (cancelled on unmount or stacked reclassify).
      setPredictionRefreshKey((k) => k + 1);
      if (predictionRefreshDelayRef.current) {
        clearTimeout(predictionRefreshDelayRef.current);
      }
      predictionRefreshDelayRef.current = setTimeout(() => {
        setPredictionRefreshKey((k) => k + 1);
        predictionRefreshDelayRef.current = null;
      }, 2500);
      messageApi.success('Issue reclassified');
    } catch (err: any) {
      messageApi.error(`Reclassify failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setReclassifyLoading(false);
    }
  }, [selectedIssue, messageApi]);

  // --- Claim / release ("I'm on it") ---
  // Applies a claim result to both the detail pane and the matching list row
  // so the lock badge appears everywhere without a refetch.
  const applyClaim = useCallback((issueId: number, claimedBy: string | null, claimedAt: string | null) => {
    setSelectedIssue((prev) => (prev && prev.issue_id === issueId ? { ...prev, claimed_by: claimedBy, claimed_at: claimedAt } : prev));
    setIssues((prev) => prev.map((i) => (i.issue_id === issueId ? { ...i, claimed_by: claimedBy, claimed_at: claimedAt } : i)));
  }, []);

  const handleClaim = useCallback(async (issueId: number, steal = false) => {
    try {
      const r = await claimIssue(issueId, steal);
      applyClaim(issueId, r.claimed_by, r.claimed_at);
      messageApi.success(r.stolen_from ? `Claimed — taken over from ${r.stolen_from}` : "Claimed — you're on it");
    } catch (err) {
      // 409 already_claimed: another operator holds a live claim. Offer to
      // take it over rather than silently failing.
      if (err instanceof ApiError && err.status === 409) {
        const holder = err.detail || 'Another operator is investigating this';
        Modal.confirm({
          title: 'Someone is already on this',
          content: `${holder}. Take it over?`,
          okText: 'Take over',
          cancelText: 'Leave it',
          onOk: () => handleClaim(issueId, true),
        });
        return;
      }
      messageApi.error(`Couldn't claim: ${(err as Error)?.message || 'error'}`);
    }
  }, [applyClaim, messageApi]);

  // Auto-claim/release: opening an issue in this queue-style, one-at-a-time
  // tool IS "doing something about it" — there's no separate lightweight
  // "start working" gesture, and the two write endpoints that do exist
  // (triage submit, reclassify) already auto-release on the server the
  // instant they fire, so they can't carry a claim through the dwell time an
  // operator spends actually investigating. Claim silently on selection,
  // release on deselect (switching to a different issue, or it closing) —
  // but NOT on navigate-away from /triage (component unmount). Losing the
  // lock just from flipping to Issues/Work Tasks and back was confusing
  // (reported 2026-07-12); the 30-min claim TTL (CLAIM_TTL_MINUTES,
  // server-side) is the real backstop for an abandoned tab/closed laptop.
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  const autoClaimedIssueIdRef = useRef<number | null>(null);
  useEffect(() => {
    const id = selectedIssue?.issue_id;
    if (id == null || selectedIssue?.closed_at) return;
    let cancelled = false;
    claimIssue(id)
      .then((r) => {
        if (cancelled) return;
        autoClaimedIssueIdRef.current = id;
        applyClaim(id, r.claimed_by, r.claimed_at);
      })
      .catch(() => {
        // Peer holds it, issue closed mid-flight, or a transient error —
        // non-fatal for an implicit claim; just don't take it over.
      });
    return () => {
      cancelled = true;
      if (!isMountedRef.current) return;
      if (autoClaimedIssueIdRef.current === id) {
        autoClaimedIssueIdRef.current = null;
        releaseIssue(id).catch(() => { /* best-effort */ });
      }
    };
  }, [selectedIssue?.issue_id, selectedIssue?.closed_at, applyClaim]);

  // --- Undo handler (uses ref to always read latest undoInfo) ---
  const handleUndo = useCallback(async () => {
    const info = undoInfoRef.current;
    if (!info) return;
    trackEvent('undo_clicked', {
      ticket_id: info.ticket.ticket_id,
      location_id: info.ticket.location_id ?? null,
      reverted_from_class_id: info.ticket.ticket_class_id ?? null,
      reverted_to_class_id: info.originalClassId ?? null,
    });
    try {
      await triageTicket(info.ticket.ticket_id, {
        ticket_class_id: info.originalClassId,
        status_id: info.originalStatusId,
        action_id: info.originalActionId,
        root_cause_id: info.originalRootCauseId,
      });
      setTickets((prev) => {
        const idx = Math.min(info.index, prev.length);
        const next = [...prev];
        next.splice(idx, 0, info.ticket);
        return next;
      });
      selectTicket(info.ticket.ticket_id);
      setPendingTaskInfo(null);
      setUndoInfo(null);
      messageApi.success('Ticket restored');
      loadLocations();
    } catch (err: any) {
      messageApi.error(`Undo failed: ${err.message}`);
    }
  }, [selectTicket, messageApi, loadLocations]);

  // --- Actions (queue-style: remove triaged ticket, advance to next in place) ---
  const handleAction = useCallback(async (actionDef: TriageActionDef, comment?: string) => {
    if (!selectedTicketId || !selectedTicket) return;
    const triagedTicket = selectedTicket;

    // Capture original index for undo. Use the ref so this callback doesn't
    // take `tickets` as a dep and re-register on every list mutation — UX-013.
    const originalIndex = ticketListRef.current.findIndex((t) => t.ticket_id === triagedTicket.ticket_id);

    await triageTicket(selectedTicketId, {
      ticket_class_id: actionDef.ticketClassId,
      action_id: actionDef.actionId,
      root_cause_id: actionDef.rootCauseId,
      status_id: actionDef.statusId,
      comment,
    });

    setSessionCount((c) => c + 1);
    if (!sessionStart) setSessionStart(Date.now());

    setUndoInfo({
      ticket: triagedTicket,
      index: originalIndex,
      originalClassId: triagedTicket.ticket_class_id,
      originalStatusId: triagedTicket.ticket_status_id,
      originalActionId: triagedTicket.action_id,
      originalRootCauseId: triagedTicket.root_cause_id,
    });
    messageApi.open({
      type: 'success',
      content: (
        <span role="status" aria-live="polite">
          Ticket #{triagedTicket.ticket_id} &rarr; {actionDef.label}{' '}
          <Typography.Link onClick={() => handleUndo()}>Undo</Typography.Link>
        </span>
      ),
      // UX-062: 5s was too short — a hardware-class triage opens the task
      // prompt modal which covers the toast, and by the time the operator
      // submits and dismisses, the undo is gone. 15s gives a realistic
      // window even with a modal interaction in between.
      duration: 15,
      // Per-ticket key so rapid successive triages don't collapse undo
      // toasts — operator can undo two actions back-to-back if they
      // realize the mistake within 15s. Previously the single 'triage-undo'
      // key meant each new toast replaced the prior, losing the earlier
      // undo handle.
      key: `triage-undo-${triagedTicket.ticket_id}`,
    });

    // Prompt task creation (not for Close or False Alert)
    if (actionDef.actionId && !getSkippedActions().has(actionDef.key)) {
      const room = triagedTicket.key_name || triagedTicket.autoset_name || '';
      const suggestedTitle = buildTaskTitle(actionDef.key, room);
      taskPromptForm.setFieldsValue({
        title: suggestedTitle,
        action_id: actionDef.actionId || undefined,
        executor: localStorage.getItem(`triage-executor-${triagedTicket.location_id}`) || 'Contractor',
        description: undefined,
        due_date: undefined,
      });
      setPendingTaskInfo({ ticket: triagedTicket, action: actionDef });
    }

    // Remove triaged ticket and advance to next at same position
    setTickets((prev) => {
      const remaining = prev.filter((t) => t.ticket_id !== selectedTicketId);
      const nextIdx = Math.min(originalIndex, remaining.length - 1);
      const nextTicket = remaining[nextIdx] ?? null;
      selectTicket(nextTicket?.ticket_id ?? null);
      return remaining;
    });

    loadLocations();
  }, [selectedTicketId, selectedTicket, sessionStart, selectTicket, loadLocations, taskPromptForm, messageApi, handleUndo]);

  // --- Task creation from triage prompt ---
  const handleTaskPromptCreate = useCallback(async (values: any) => {
    if (!pendingTaskInfo) return;
    setTaskPromptCreating(true);
    try {
      // If user added a triage comment, update the ticket with it
      if (values.comment) {
        await triageTicket(pendingTaskInfo.ticket.ticket_id, { comment: values.comment });
      }
      const payload: CreateTaskPayload = {
        support_ticket_id: pendingTaskInfo.ticket.ticket_id,
        title: values.title,
        description: values.description || undefined,
        due_date: values.due_date ? values.due_date.toISOString() : undefined,
        action_id: values.action_id ?? pendingTaskInfo.action.actionId ?? undefined,
        executor: values.executor || undefined,
        parent_entity_type: 'node_to_slot_mapping',
        parent_entity_id: pendingTaskInfo.ticket.observable_id,
      };
      await createTask(payload);
      if (values.executor && pendingTaskInfo.ticket.location_id) {
        localStorage.setItem(`triage-executor-${pendingTaskInfo.ticket.location_id}`, values.executor);
      }
      trackEvent('task_created', {
        ticket_id: pendingTaskInfo.ticket.ticket_id,
        location_id: pendingTaskInfo.ticket.location_id ?? null,
        action_id: payload.action_id ?? null,
        action_key: pendingTaskInfo.action.key,
        executor: values.executor ?? null,
        source: 'post_action',
      });
      messageApi.success('Task created');
    } catch (err: any) {
      messageApi.error(`Failed to create task: ${err.message}`);
    } finally {
      setTaskPromptCreating(false);
      setPendingTaskInfo(null);
      taskPromptForm.resetFields();
    }
  }, [pendingTaskInfo, messageApi, taskPromptForm]);

  // --- Log handlers ---
  const handleAddLocationLog = useCallback(async (text: string) => {
    if (!selectedTicket) return;
    const log = await addLocationLog(selectedTicket.location_id, text);
    setLocationLogs((prev) => [log, ...prev]);
  }, [selectedTicket]);

  const handleAddNodeLog = useCallback(async (text: string) => {
    if (!selectedTicket) return;
    const log = await addNodeLog(selectedTicket.observable_id, text);
    setNodeLogs((prev) => [log, ...prev]);
  }, [selectedTicket]);

  // --- Navigation ---
  const navigateTicket = useCallback((direction: number) => {
    const list = ticketListRef.current;
    if (list.length === 0) return;
    const idx = list.findIndex((t) => t.ticket_id === selectedTicketId);
    const nextIdx = Math.max(0, Math.min(list.length - 1, idx + direction));
    selectTicket(list[nextIdx].ticket_id);
  }, [selectedTicketId, selectTicket, ticketListRef]);

  const navigateIssue = useCallback((direction: number) => {
    if (issues.length === 0) return;
    const idx = issues.findIndex((i) => i.issue_id === issueId);
    const nextIdx = Math.max(0, Math.min(issues.length - 1, idx + direction));
    const next = issues[nextIdx];
    if (next) {
      setIssueId(next.issue_id);
      setParam('issue', String(next.issue_id));
    }
  }, [issues, issueId, setParam]);

  // `n` — jump to the next issue in the queue that isn't a peer's live claim,
  // so a keyboard-driven operator skips work someone else already started.
  const jumpNextUnclaimed = useCallback(() => {
    if (issues.length === 0) return;
    const idx = issues.findIndex((i) => i.issue_id === issueId);
    const next = pickNextIssue(issues, idx + 1);
    if (next) {
      setIssueId(next.issue_id);
      setParam('issue', String(next.issue_id));
    }
  }, [issues, issueId, pickNextIssue, setParam]);

  // Dispatch 1-8. Both /triage and /issues now drive off the selected issue
  // (the bulk-ticket flow was removed). Prefer the issue path whenever an
  // issue row is selected; fall back to the ticket path for legacy ticket
  // selection (only reachable when an issue is NOT selected — e.g. directly
  // clicking a ticket row in the alerts tab on /triage).
  const dispatchHotkey = useCallback((idx: number) => {
    const actionDef = TRIAGE_ACTIONS[idx];
    if (!actionDef) return;
    if (selectedIssue) {
      void handleIssueAction(actionDef);
      return;
    }
    if (selectedTicket) {
      void handleAction(actionDef);
    }
  }, [selectedIssue, selectedTicket, handleIssueAction, handleAction]);

  // Clear any pending ticket selection when the operator switches to /issues
  // — selection state lives in the TriageContext, so without this it survives
  // the route change and the bulk action bar would re-appear (and hotkeys
  // could target stale tickets).
  useEffect(() => {
    // No-op: bulk ticket multi-select was removed. Kept as a marker that
    // route-switching no longer needs cross-route selection cleanup.
  }, [mode]);

  // --- Collapsible panels ---
  // Below these viewport widths the two fixed-width sidebars (240px + 360px)
  // leave too little room for the detail pane, so auto-collapse them —
  // narrowest first. TICKETS_COLLAPSE_BREAKPOINT was originally 1300, which
  // collapsed the issue list at the very common 1280px laptop width by
  // default; that read as "I can't select anything at all" (reported
  // 2026-07-12) because the collapsed rail is easy to miss. Lowered so a
  // 1280px window keeps the issue list open; only genuinely tight widths
  // (~1024px and below) collapse both.
  const TICKETS_COLLAPSE_BREAKPOINT = 1150;
  const LOCATION_COLLAPSE_BREAKPOINT = 1050;
  const viewportWidth = useViewportWidth();
  const [locationCollapsed, setLocationCollapsed] = useState(() => window.innerWidth < LOCATION_COLLAPSE_BREAKPOINT);
  const [ticketsCollapsed, setTicketsCollapsed] = useState(() => window.innerWidth < TICKETS_COLLAPSE_BREAKPOINT);

  // Auto collapse/expand only when the viewport crosses one of the
  // breakpoints above, so a manual re-open at a given width persists until
  // the user resizes past that boundary again (rather than fighting every
  // resize tick within the same band).
  const collapseBandRef = useRef({
    loc: window.innerWidth < LOCATION_COLLAPSE_BREAKPOINT,
    tickets: window.innerWidth < TICKETS_COLLAPSE_BREAKPOINT,
  });
  useEffect(() => {
    const locBand = viewportWidth < LOCATION_COLLAPSE_BREAKPOINT;
    const ticketsBand = viewportWidth < TICKETS_COLLAPSE_BREAKPOINT;
    if (locBand !== collapseBandRef.current.loc) {
      setLocationCollapsed(locBand);
    }
    if (ticketsBand !== collapseBandRef.current.tickets) {
      setTicketsCollapsed(ticketsBand);
    }
    collapseBandRef.current = { loc: locBand, tickets: ticketsBand };
  }, [viewportWidth]);

  useKeyboardShortcuts({
    'ArrowUp': () => (mode === 'issues' ? navigateIssue(-1) : navigateTicket(-1)),
    'ArrowDown': () => (mode === 'issues' ? navigateIssue(1) : navigateTicket(1)),
    '1': () => dispatchHotkey(0),
    '2': () => dispatchHotkey(1),
    '3': () => dispatchHotkey(2),
    '4': () => dispatchHotkey(3),
    '5': () => dispatchHotkey(4),
    '6': () => dispatchHotkey(5),
    '7': () => dispatchHotkey(6),
    '8': () => dispatchHotkey(7),
    // TRIAGE_ACTIONS has 8 entries (1-8). '9' was a dead binding pointing
    // at TRIAGE_ACTIONS[8] which dispatchHotkey early-returns on. Removed.
    'n': () => jumpNextUnclaimed(),
    'Shift+?': () => setShortcutHelpOpen(true),
    'Ctrl+k': () => setCommandPaletteOpen(true),
  });

  return (
    <>
      {contextHolder}

      <TriageTour open={tourOpen} onClose={closeTour} />

      {/* Session counter bar — role=status + aria-live='polite' so screen
          readers announce "N triaged this session" after each triage. */}
      {sessionCount > 0 && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0', padding: '3px 16px', fontSize: 11, color: '#595959', display: 'flex', gap: 16, alignItems: 'center' }}
        >
          <span><strong>{sessionCount}</strong> triaged this session</span>
          {sessionStart && (
            <span>{Math.round(sessionCount / ((Date.now() - sessionStart) / 3600000))} tickets/hr</span>
          )}
          <span>{tickets.length} remaining</span>
        </div>
      )}

      {/* Keyboard shortcut cheat sheet */}
      <Modal
        title="Keyboard Shortcuts"
        open={shortcutHelpOpen}
        onCancel={() => setShortcutHelpOpen(false)}
        footer={null}
        width={400}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 13 }}>
          {[
            ['\u2191 / \u2193', 'Navigate tickets'],
            ['1', 'Hardware Issue'],
            ['2', 'Tampering / Damage'],
            ['3', 'Hardware EOL'],
            ['4', 'Firmware Issue'],
            ['5', 'Out of Range'],
            ['6', 'On-site Investigation'],
            ['7', 'False Alert'],
            ['8', 'Close Ticket'],
            ['n', 'Next unclaimed issue'],
            ['?', 'Show this help'],
          ].map(([key, desc]) => (
            <React.Fragment key={key}>
              <kbd style={{ background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 4, padding: '2px 8px', fontFamily: 'monospace', fontWeight: 600 }}>{key}</kbd>
              <span>{desc}</span>
            </React.Fragment>
          ))}
        </div>
      </Modal>

      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        locations={locations}
        onSelectLocation={selectLocation}
        onAction={handleAction}
        onSetFilter={setParam}
      />

      {/* hasSider forced: antd's Layout only applies flex-direction:row when a
          real <Sider> child is present. Both location and issue panels swap
          to a plain collapsed-rail <div> at narrow widths, and when both are
          collapsed at once no <Sider> remains — Layout silently falls back to
          its column default, stacking the rails and detail pane full-width
          instead of side by side. */}
      <Layout hasSider style={{ height: '100%' }}>

      {/* Post-triage task creation prompt */}
      <Modal
        title={`Create task for: ${pendingTaskInfo?.action.label || ''}`}
        open={!!pendingTaskInfo}
        onCancel={() => { setPendingTaskInfo(null); taskPromptForm.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={taskPromptForm} layout="vertical" onFinish={handleTaskPromptCreate}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input placeholder="Task title" />
          </Form.Item>
          <Form.Item name="action_id" label="Action">
            <Select
              placeholder="Select an action"
              allowClear
              showSearch
              optionFilterProp="label"
              dropdownRender={actionDropdownRender}
              options={actions.map((a) => ({ value: a.id, label: a.action }))}
            />
          </Form.Item>
          <Form.Item name="executor" label="Executor">
            <Select options={EXECUTOR_OPTIONS} placeholder="Select executor" />
          </Form.Item>
          <Form.Item name="due_date" label="Due Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Task description..." />
          </Form.Item>
          <Form.Item name="comment" label="Triage Comment">
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder="Optional note about this classification..." />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setPendingTaskInfo(null); taskPromptForm.resetFields(); }}>Skip</Button>
              <Button type="primary" htmlType="submit" loading={taskPromptCreating}>Create Task</Button>
            </Space>
          </Form.Item>
        </Form>
        <div style={{ marginBottom: 12 }}>
          <Checkbox onChange={() => toggleSkipAction(pendingTaskInfo?.action.key || '')}>
            Don't show task prompt for "{pendingTaskInfo?.action.label}" actions
          </Checkbox>
        </div>
      </Modal>

      {/* Post-confirm task suggestion prompt (Issues flow) */}
      <Modal
        title={`Suggested task for issue #${pendingIssueTaskInfo?.issue.issue_id || ''}: ${pendingIssueTaskInfo?.action.label || ''}`}
        open={!!pendingIssueTaskInfo}
        onCancel={() => { setPendingIssueTaskInfo(null); taskPromptForm.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={taskPromptForm} layout="vertical" onFinish={handleIssueTaskCreate}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input placeholder="Task title" />
          </Form.Item>
          <Form.Item name="action_id" label="Action">
            <Select
              placeholder="Select an action"
              allowClear
              showSearch
              optionFilterProp="label"
              dropdownRender={actionDropdownRender}
              options={actions.map((a) => ({ value: a.id, label: a.action }))}
            />
          </Form.Item>
          <Form.Item name="executor" label="Executor">
            <Select options={EXECUTOR_OPTIONS} placeholder="Select executor" />
          </Form.Item>
          <Form.Item name="due_date" label="Due Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Task description..." />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setPendingIssueTaskInfo(null); taskPromptForm.resetFields(); }}>Skip</Button>
              <Button type="primary" htmlType="submit" loading={taskPromptCreating}>Create Task</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Location Panel */}
      {locationCollapsed ? (
        <CollapsedRail label="Locations" onExpand={() => setLocationCollapsed(false)} />
      ) : (
        <Sider
          width={240}
          style={{ background: '#fff', borderRight: '1px solid #f0f0f0', overflow: 'hidden' }}
          trigger={null}
        >
          <div data-tour="locations" style={{ height: '100%' }}>
            <LocationPane locations={locations} loading={locationsLoading} mode={mode} onCollapse={() => setLocationCollapsed(true)} />
          </div>
        </Sider>
      )}

      {/* Ticket / Issue List Panel */}
      {ticketsCollapsed ? (
        <CollapsedRail label="Issues" onExpand={() => setTicketsCollapsed(false)} />
      ) : (
        <Sider
          width={360}
          style={{ background: '#fff', borderRight: '1px solid #f0f0f0', overflow: 'hidden' }}
          trigger={null}
        >
          <div data-tour="issues" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: 1, minHeight: 0 }}>
              <IssueListPane
                issues={issues}
                loading={issuesLoading}
                selectedIssueId={issueId}
                statusFilter={statusFilter}
                classFilter={classFilter}
                sortOrder={sortOrder}
                mode={mode === 'triage' ? 'nodes' : 'issues'}
                currentUserEmail={currentUserEmail}
                selectable={mode === 'issues'}
                selectedIds={selectedIssueIds}
                onToggleSelect={toggleSelectIssue}
                onClearSelection={() => setSelectedIssueIds(new Set())}
                onBulkAction={handleBulkAction}
                bulkRunning={bulkRunning}
                onSelectIssue={(id) => { setIssueId(id); setParam('issue', id != null ? String(id) : null); }}
                onStatusFilterChange={(val) => setParam('status', val === 'open' ? null : val)}
                onClassFilterChange={(val) => setParam('class', val ? String(val) : null)}
                onSortChange={(val) => setParam('sort', val === 'priority' ? null : val)}
                onBackfill={handleBackfillIssues}
                {...(isAdmin
                  ? { onReclassifyAll: handleReclassifyAll, reclassifyAllRunning }
                  : {})}
                onCollapse={() => setTicketsCollapsed(true)}
                hasMore={issuesHasMore}
                loadingMore={issuesLoadingMore}
                onLoadMore={loadMoreIssues}
                totalCount={(() => {
                  const loc = locations.find((l) => l.location_id === selectedLocationId);
                  if (!loc?.issueCounts) return undefined;
                  return mode === 'triage' ? loc.issueCounts.suggested : loc.issueCounts.confirmed;
                })()}
                onApplyView={(view) => {
                  setParam('status', view.status);
                  setParam('class', view.class);
                  setParam('sort', view.sort);
                }}
              />
            </div>
          </div>
        </Sider>
      )}

      {/* Detail Panel */}
      <Content style={{ background: '#f0f2f5', overflow: 'hidden' }}>
        <IssueDetailPane
          issue={selectedIssue}
          alerts={issueAlerts}
          alertsLoading={issueAlertsLoading}
          nodeDetail={nodeDetail}
          batteryStatus={batteryStatus}
          batteryAtOffline={batteryAtOffline}
          gateway={gateway}
          recovery={recovery}
          nodeHistory={nodeHistory}
          firmwareHistory={firmwareHistory}
          testJigReports={testJigReports}
          locationLogs={locationLogs}
          nodeLogEntries={nodeLogs}
          tasks={tasks}
          loading={issueDetailLoading || detailLoading}
          mode={mode}
          onAddLocationLog={handleAddLocationLog}
          onAddNodeLog={handleAddNodeLog}
          onIssueAction={handleIssueAction}
          onReclassify={handleReclassify}
          reclassifyLoading={reclassifyLoading}
          onConfirm={handleConfirm}
          currentUserEmail={currentUserEmail}
          onClaim={handleClaim}
          onTaskCreated={(task) => setTasks((prev) => [task, ...prev])}
          onTaskUpdated={(task) => setTasks((prev) => prev.map((t) => t.id === task.id ? task : t))}
          onTaskDeleted={(taskId) => setTasks((prev) => prev.filter((t) => t.id !== taskId))}
          predictionRefreshKey={predictionRefreshKey}
        />
      </Content>
    </Layout>
    </>
  );
};

export default TriagePage;
